/**
 * Helpers for financial values that may be masked by the backend for some roles
 * (e.g. "xxxxxx", "****"). Masked values are passed through untouched so that
 */

const NUMERIC_PATTERN = /^-?\d+(\.\d+)?$/;

/** True when the value is a non-empty string that is not a valid number (e.g. "xxxxxx"). */
export const isMasked = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  return !NUMERIC_PATTERN.test(trimmed.replace(/[,$₹\s]/g, ""));
};

/** Returns the first masked value found in the list, otherwise null. */
export const findMasked = (...values) => values.find(isMasked) ?? null;

/** Converts to a number, but returns the masked string as-is when masked. */
export const toAmount = (value) => {
  if (isMasked(value)) return value.trim();
  const num = Number(typeof value === "string" ? value.replace(/[,$₹\s]/g, "") : value ?? 0);
  return Number.isFinite(num) ? num : 0;
};

/** Sums amounts; if any item is masked the masked value is returned instead. */
export const sumAmounts = (values) => {
  const list = Array.isArray(values) ? values : [];
  const masked = findMasked(...list);
  if (masked) return masked;
  return list.reduce((sum, v) => sum + toAmount(v), 0);
};

/**
 * Runs a calculation only if none of the inputs are masked.
 * Otherwise returns the masked value (or the provided fallback).
 */
export const safeCalc = (values, calc, maskedFallback) => {
  const masked = findMasked(...values);
  if (masked) return maskedFallback !== undefined ? maskedFallback : masked;
  return calc(...values.map((v) => toAmount(v)));
};

/** Formats via `formatter` for numbers, returns masked value untouched. */
export const formatMaskable = (value, formatter) =>
  isMasked(value) ? value.trim() : formatter(toAmount(value));
